import { ArrowRight, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@eduviaos/ui-kit";
import { timeGreetingLocalized } from "../../lib/i18n/portalShell";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

export function StudyCentreHero({
  firstName,
  mascotSrc,
  onGetStarted,
  showCta = true,
}: {
  firstName: string;
  mascotSrc: string;
  onGetStarted: () => void;
  /** Hidden once a plan exists — Continue learning is that deep link. */
  showCta?: boolean;
}) {
  const reduce = useReducedMotion();
  const { locale, strings } = usePortalLocale();
  const greeting = timeGreetingLocalized(locale);

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.28, ease: EASE_OUT }}
      className={cn(
        "hero-orbs hero-student relative overflow-hidden rounded-[1.75rem] px-5 pb-0 pt-6 text-white shadow-[var(--shadow-float)] sm:px-6 sm:pt-7",
      )}
    >
      <Sparkles className="absolute left-[42%] top-5 size-4 text-white/35" aria-hidden="true" />
      <Sparkles className="absolute right-[28%] top-8 size-3 text-white/25" aria-hidden="true" />

      <div className="relative z-10 flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1 pb-6">
          <p className="text-white/88 text-sm font-medium">{greeting},</p>
          <h1 className="font-display mt-0.5 text-[1.75rem] font-semibold leading-tight sm:text-[2rem]">
            {firstName}! 👋
          </h1>
          <p className="mt-2 text-sm font-medium text-white/85 sm:text-base">
            {strings.heroTagline}
          </p>
          {showCta ? (
            <button
              type="button"
              onClick={onGetStarted}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-[color-mix(in_oklch,var(--color-primary)_75%,black)] shadow-sm transition-transform duration-150 ease-out hover:-translate-y-0.5 active:scale-[0.97] motion-reduce:transform-none"
            >
              {strings.heroCta}
              <ArrowRight className="size-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <motion.img
          src={mascotSrc}
          alt=""
          aria-hidden="true"
          loading="eager"
          initial={reduce ? false : { opacity: 0, x: 16, y: 8 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.28, ease: EASE_OUT, delay: 0.04 }}
          className="pointer-events-none -mb-1 h-[9.5rem] w-[7.5rem] shrink-0 self-end object-contain object-bottom drop-shadow-xl sm:h-[10.5rem] sm:w-[8.5rem]"
        />
      </div>
    </motion.div>
  );
}
